// User-facing single-ticket thread. Renders the shared TicketThread with a
// reply box. A closed ticket still allows sending — the backend reopens it.

import { TicketStatusBadge } from '@/components/support/TicketStatusBadge';
import { TicketThread } from '@/components/support/TicketThread';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useReplyToTicket, useTicket } from '@/hooks/useSupport';
import { extractApiError } from '@/lib/api';
import { ArrowLeft, Loader2, Send } from 'lucide-react';
import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';

export default function SupportTicket() {
  const { id } = useParams<{ id: string }>();
  const detail = useTicket(id);
  const reply = useReplyToTicket(id ?? '');
  const [draft, setDraft] = React.useState('');

  function send() {
    const body = draft.trim();
    if (!body) {
      toast.error('Type a message first.');
      return;
    }
    reply.mutate(body, {
      onSuccess: () => setDraft(''),
      onError: (err) => toast.error(extractApiError(err)?.message ?? 'Could not send reply.'),
    });
  }

  const ticket = detail.data?.ticket;
  const isClosed = ticket?.status === 'closed';

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <Link
        to="/support"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Back to tickets
      </Link>

      {detail.isLoading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : detail.isError || !detail.data || !ticket ? (
        <div className="rounded-md border bg-destructive/5 p-4 text-sm text-destructive">
          {extractApiError(detail.error)?.message ?? 'Could not load this ticket.'}
        </div>
      ) : (
        <>
          <header className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h1 className="text-2xl font-semibold tracking-tight"><span className="text-muted-foreground">#{ticket.number}</span> {ticket.subject}</h1>
              <TicketStatusBadge status={ticket.status} />
            </div>
            {isClosed ? (
              <p className="text-sm text-muted-foreground">
                This ticket is closed. Sending a reply will reopen it.
              </p>
            ) : null}
          </header>

          <TicketThread messages={detail.data.messages} viewer="customer" />

          <div className="space-y-2 border-t pt-4">
            <Textarea
              placeholder={isClosed ? 'Reply to reopen this ticket…' : 'Write a reply…'}
              className="min-h-[120px]"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              disabled={reply.isPending}
            />
            <div className="flex justify-end">
              <Button onClick={send} disabled={reply.isPending}>
                <Send className="mr-2 h-4 w-4" />
                {reply.isPending ? 'Sending…' : 'Send'}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
